"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { createClient } from "@/lib/supabase/client"
import { useRouter } from 'next/navigation'
import { CheckCircle2, Copy, Eye, EyeOff, Clock, ImageIcon, Headphones, FileText, Puzzle, Sparkles } from 'lucide-react'
import { LoginModal } from "@/components/login-modal"

interface StudentSignupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const LEARNING_PREFERENCES = [
  {
    id: 'need_more_time',
    label: "J'ai besoin de plus de temps",
    icon: Clock,
    color: 'from-cyan-400 to-blue-500',
    glowColor: 'shadow-cyan-500/50',
    hoverGlow: 'hover:shadow-cyan-500/80',
    borderGlow: 'border-cyan-400/50',
    selectedBorder: 'border-cyan-400',
    iconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
  },
  {
    id: 'visual_learner',
    label: 'Je préfère les explications visuelles',
    icon: ImageIcon,
    color: 'from-fuchsia-400 to-purple-500',
    glowColor: 'shadow-fuchsia-500/50',
    hoverGlow: 'hover:shadow-fuchsia-500/80',
    borderGlow: 'border-fuchsia-400/50',
    selectedBorder: 'border-fuchsia-400',
    iconBg: 'bg-gradient-to-br from-fuchsia-500 to-purple-600',
  },
  {
    id: 'auditory_learner',
    label: "J'apprends mieux à l'oral",
    icon: Headphones,
    color: 'from-emerald-400 to-green-500',
    glowColor: 'shadow-emerald-500/50',
    hoverGlow: 'hover:shadow-emerald-500/80',
    borderGlow: 'border-emerald-400/50',
    selectedBorder: 'border-emerald-400',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-green-600',
  },
  {
    id: 'writing_difficulties',
    label: "J'ai des difficultés avec l'écrit",
    icon: FileText,
    color: 'from-amber-400 to-orange-500',
    glowColor: 'shadow-amber-500/50',
    hoverGlow: 'hover:shadow-amber-500/80',
    borderGlow: 'border-amber-400/50',
    selectedBorder: 'border-amber-400',
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
  },
  {
    id: 'needs_sequencing_and_feedback',
    label: "J'ai besoin d'etapes claires et de feedback",
    icon: Puzzle,
    color: 'from-violet-400 to-indigo-500',
    glowColor: 'shadow-violet-500/50',
    hoverGlow: 'hover:shadow-violet-500/80',
    borderGlow: 'border-violet-400/50',
    selectedBorder: 'border-violet-400',
    iconBg: 'bg-gradient-to-br from-violet-500 to-indigo-600',
  },
  {
    id: 'needs_reading_support',
    label: "J'ai besoin d'aide pour la lecture",
    icon: Sparkles,
    color: 'from-rose-400 to-pink-500',
    glowColor: 'shadow-rose-500/50',
    hoverGlow: 'hover:shadow-rose-500/80',
    borderGlow: 'border-rose-400/50',
    selectedBorder: 'border-rose-400',
    iconBg: 'bg-gradient-to-br from-rose-500 to-pink-600',
  },
]

const GRADE_LEVELS = [
  { value: 'Terminale', label: 'Terminale' },
  { value: '1ere', label: '1ère' },
  { value: '2nde', label: '2nde' },
  { value: '3eme', label: '3ème' },
  { value: '4eme', label: '4ème' },
  { value: '5eme', label: '5ème' },
  { value: '6eme', label: '6ème' },
  { value: 'CM2', label: 'CM2' },
  { value: 'CM1', label: 'CM1' },
  { value: 'CE2', label: 'CE2' },
  { value: 'CE1', label: 'CE1' },
]

export function StudentSignupModal({ open, onOpenChange }: StudentSignupModalProps) {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [schoolCode, setSchoolCode] = useState("")
  const [gradeLevel, setGradeLevel] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [credentials, setCredentials] = useState<{
    loginIdentifier: string
    password: string
    email: string
    hasRealEmail: boolean
    userEmail?: string // Add userEmail field to store the real user email separately
  } | null>(null)
  const [showPassword, setShowPassword] = useState(true)
  const [copied, setCopied] = useState(false)
  const router = useRouter()
  const [learningPreferences, setLearningPreferences] = useState<string[]>([])
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const togglePreference = (id: string) => {
    setLearningPreferences(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    )
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const normalizedSchoolCode = schoolCode.trim().toUpperCase()
      
      console.log('[v0] Sending signup request with:', {
        firstName,
        lastName,
        email: email || '(empty)',
        schoolCode: normalizedSchoolCode,
        gradeLevel,
        preferencesCount: learningPreferences.length
      })
      
      const response = await fetch('/api/auth/student-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim() || null,
          schoolCode: normalizedSchoolCode,
          gradeLevel,
          learningPreferences,
        }),
      })

      const data = await response.json()
      
      console.log('[v0] Signup response:', {
        status: response.status,
        ok: response.ok,
        data
      })

      if (!response.ok) {
        if (response.status === 429) {
          setBlockedUntil(data.blockedUntil ? new Date(data.blockedUntil) : null)
        }
        if (data.attemptsLeft !== undefined) {
          setAttemptsLeft(data.attemptsLeft)
        }
        throw new Error(data.error || 'Erreur lors de la création du compte')
      }

      setCredentials({
        loginIdentifier: data.loginIdentifier,
        password: data.defaultPassword,
        email: data.email,
        hasRealEmail: data.hasRealEmail,
        userEmail: email.trim() || undefined, // Store the actual email the user entered
      })
      setShowSuccess(true)
      setAttemptsLeft(null)
      setBlockedUntil(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyCredentials = () => {
    if (!credentials) return
    
    const identifier = credentials.userEmail || credentials.loginIdentifier
    const text = `Identifiant: ${identifier}\nMot de passe: ${credentials.password}`
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleContinue = async () => {
    if (!credentials) return
    
    setIsLoading(true)
    try {
      const supabase = createClient()
      const identifier = credentials.userEmail || credentials.loginIdentifier
      
      const { error } = await supabase.auth.signInWithPassword({
        email: identifier.includes('@') ? identifier : `${identifier}@tuteur.ai`,
        password: credentials.password,
      })

      if (error) {
        console.error('[v0] Auto-login failed:', error)
        // If auto-login fails, fallback to showing login modal
        onOpenChange(false)
        setShowLoginModal(true)
        return
      }

      // Success - redirect to /devoirs
      onOpenChange(false)
      router.push('/devoirs')
    } catch (error) {
      console.error('[v0] Auto-login error:', error)
      // Fallback to login modal on error
      onOpenChange(false)
      setShowLoginModal(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setShowSuccess(false)
    setCredentials(null)
    setFirstName("")
    setLastName("")
    setEmail("")
    setSchoolCode("")
    setGradeLevel("")
    setError(null)
    setLearningPreferences([])
    setAttemptsLeft(null)
    setBlockedUntil(null)
  }

  if (showSuccess && credentials) {
    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-md bg-slate-900/95 backdrop-blur-xl border-2 border-slate-700">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="rounded-full bg-green-500/20 p-3 ring-2 ring-green-500/50 shadow-lg shadow-green-500/30">
                  <CheckCircle2 className="h-8 w-8 text-green-400" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl text-white font-bold">Compte créé avec succès!</DialogTitle>
              <DialogDescription className="text-center text-slate-300 text-base">
                Voici tes identifiants de connexion. Note-les bien!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-white">
                  {credentials.userEmail ? "Ton email de connexion" : "Ton identifiant de connexion"}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={credentials.userEmail || credentials.loginIdentifier}
                    readOnly
                    className="font-mono text-lg font-bold bg-blue-950/50 border-blue-500/50 text-blue-100"
                  />
                </div>
                <p className="text-xs text-slate-300">
                  {credentials.userEmail 
                    ? "Utilise cet identifiant ou ton email pour te connecter" 
                    : "Utilise cet identifiant pour te connecter"}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-white">Ton mot de passe</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={credentials.password}
                    type={showPassword ? "text" : "password"}
                    readOnly
                    className="font-mono text-lg font-bold bg-orange-950/50 border-orange-500/50 text-orange-100"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                    className="border-slate-600 hover:bg-slate-800 hover:border-slate-500"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-slate-300" /> : <Eye className="h-4 w-4 text-slate-300" />}
                  </Button>
                </div>
                <p className="text-sm font-semibold text-orange-200">
                  Écris ce mot de passe sur une feuille ou recopie-le maintenant, car il ne sera plus affiché ensuite.
                </p>
                <p className="text-xs text-slate-300">
                  Tu pourras le changer plus tard dans ton profil
                </p>
              </div>

              <Button
                onClick={handleCopyCredentials}
                variant="outline"
                className="w-full border-slate-600 hover:bg-slate-800 hover:border-slate-500 text-slate-200"
              >
                <Copy className="h-4 w-4 mr-2" />
                {copied ? "Copié!" : "Copier les identifiants"}
              </Button>

              <Button
                onClick={handleContinue}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-blue-500/30"
              >
                {isLoading ? "Connexion..." : "Continuer vers la connexion"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} /> */}
      </>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-2xl">Créer mon compte élève</DialogTitle>
          <DialogDescription>
            Remplis le formulaire pour rejoindre ta classe
          </DialogDescription>
        </DialogHeader>
        
        <div 
          className="overflow-y-scroll px-6 pb-6 max-h-[calc(90vh-120px)]"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#475569 #1e293b',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <style jsx>{`
            div::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }
            div::-webkit-scrollbar-track {
              background: #1e293b;
              border-radius: 4px;
            }
            div::-webkit-scrollbar-thumb {
              background: #475569;
              border-radius: 4px;
              border: 2px solid #1e293b;
            }
            div::-webkit-scrollbar-thumb:hover {
              background: #64748b;
            }
          `}</style>
          <form onSubmit={handleSignup} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="schoolCode">Code École *</Label>
              <Input
                id="schoolCode"
                type="text"
                placeholder="ECOLE2024"
                required
                value={schoolCode}
                onChange={(e) => setSchoolCode(e.target.value)}
                className="uppercase"
              />
              <p className="text-xs text-muted-foreground">
                Demande ton code école à ton professeur
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="firstName">Prénom *</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Pierre"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Nom de famille *</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Dupont"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gradeLevel">Classe *</Label>
              <Select value={gradeLevel} onValueChange={setGradeLevel} required>
                <SelectTrigger id="gradeLevel">
                  <SelectValue placeholder="Sélectionne ta classe" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_LEVELS.map((grade) => (
                    <SelectItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Dans quelle classe es-tu cette année?
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email (optionnel)</Label>
              <Input
                id="email"
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Si tu n'as pas d'email, laisse ce champ vide
              </p>
            </div>



            {attemptsLeft !== null && attemptsLeft < 3 && (
              <div className="rounded-lg bg-orange-50 border-2 border-orange-200 p-3 text-sm text-orange-800">
                <strong>Attention:</strong> {attemptsLeft} tentative{attemptsLeft > 1 ? 's' : ''} restante{attemptsLeft > 1 ? 's' : ''} avant blocage de 30 minutes.
                <br />Vérifie ton code école avec ton professeur.
              </div>
            )}

            {blockedUntil && (
              <div className="rounded-lg bg-red-50 border-2 border-red-200 p-4 text-sm text-red-800">
                <strong>Compte temporairement bloqué</strong><br />
                Trop de tentatives échouées. Vérifie ton code école avec ton professeur.
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-200 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="relative rounded-lg bg-slate-900/50 backdrop-blur-sm border-2 border-blue-400/50 p-4 text-xs text-blue-100 shadow-lg shadow-blue-500/20">
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/10 to-indigo-500/10 -z-10" />
              <strong className="text-blue-300">Ton identifiant sera:</strong> Première lettre de ton prénom + nom de famille<br />
              Exemple: Pierre Dupont → <code className="font-mono font-bold text-blue-400 bg-slate-950/50 px-2 py-0.5 rounded">pdupont</code><br />
              <strong className="text-blue-300">Ton mot de passe:</strong> Un mot de passe est créé pour toi et s'affiche à l'écran suivant. Note-le bien.
            </div>

            <Button
              type="submit"
              className="relative w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-lg overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || blockedUntil !== null}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl bg-gradient-to-r from-blue-500 to-indigo-500 -z-10" />
              <span className="relative z-10">{isLoading ? "Création du compte..." : "Créer mon compte"}</span>
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
